import { Eye, Search } from 'lucide-react'
import { useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import { useUISettingsStore } from '@/store/useUISettingsStore'

import {
  ALL_SECTIONS,
  buildOccurrenceMap,
  collectGlossary,
  collectRelations,
  groupOccurrencesBySection,
  occurrencesOf,
  relationsOf,
} from './book-ai-position'
import { GlossaryItem } from './glossary-item'
import { GLOSSARY_KINDS, type GlossaryKind, glossaryKindConfig } from './glossary-kinds'
import { RelationGraph } from './relation-graph'
import type { BookAiReaderState } from './useBookAi'

type Props = {
  bookAi: BookAiReaderState
  onNavigate: (key: number) => void
}

type KindFilter = GlossaryKind | 'all'
type View = 'list' | 'graph'

const FilterChip = ({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs',
      active ? 'border-primary bg-primary text-primary-foreground' : 'text-muted-foreground',
    )}
  >
    {children}
  </button>
)

/**
 * 登場人物・用語集
 *
 * 既定は読んだ所までのもの（読み終えた章の説明・今の位置までの登場箇所）だけを出す（ネタバレ防止）。
 * 今読んでいる章の説明は章の終わりまでの内容から書かれているため、読者が開いたときだけ含める。
 * 設定でネタバレ防止を切ると、本全体の人物・用語・関係・登場箇所を出す。
 */
export const GlossaryTab = ({ bookAi, onNavigate }: Props) => {
  const { sections, currentSectionIndex, currentKey, status } = bookAi
  const spoilerGuard = useUISettingsStore((state) => state.glossarySpoilerGuard)
  const setSpoilerGuard = useUISettingsStore((state) => state.setGlossarySpoilerGuard)
  const [query, setQuery] = useState('')
  const [kindFilter, setKindFilter] = useState<KindFilter>('all')
  const [view, setView] = useState<View>('list')
  const [includeCurrent, setIncludeCurrent] = useState(false)
  const uptoIndex = !spoilerGuard
    ? ALL_SECTIONS
    : includeCurrent
      ? Math.max(currentSectionIndex, 0)
      : currentSectionIndex - 1

  const entries = useMemo(() => collectGlossary(sections, uptoIndex), [sections, uptoIndex])
  const relations = useMemo(() => collectRelations(sections, uptoIndex), [sections, uptoIndex])
  const occurrenceMap = useMemo(() => buildOccurrenceMap(status?.term_index ?? []), [status?.term_index])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return entries
    return entries.filter(
      (entry) =>
        entry.name.toLowerCase().includes(q) ||
        entry.aliases.some((alias) => alias.toLowerCase().includes(q)) ||
        entry.notes.some((note) => note.description.toLowerCase().includes(q)),
    )
  }, [entries, query])

  // 種類ごとに分けた一覧（無い種類は出さない）
  const groups = useMemo(
    () =>
      GLOSSARY_KINDS.map((config) => ({
        ...config,
        entries: filtered.filter((entry) => glossaryKindConfig(entry.kind).kind === config.kind),
      })).filter((group) => group.entries.length > 0),
    [filtered],
  )
  const visibleGroups = kindFilter === 'all' ? groups : groups.filter((group) => group.kind === kindFilter)

  const occurrencesFor = (name: string) => {
    const keys = occurrencesOf(occurrenceMap, name)
    const readable = spoilerGuard ? keys.filter((key) => currentKey != null && key <= currentKey) : keys
    return groupOccurrencesBySection(sections, readable)
  }

  return (
    <div className="space-y-3">
      <label className="flex items-center justify-between gap-3 rounded-lg bg-muted/50 p-2.5">
        <span className="min-w-0">
          <span className="block text-sm font-medium">読んだ所までだけ表示</span>
          <span className="block text-xs text-muted-foreground">
            オフにすると、本全体の人物・用語・関係を表示します（ネタバレに注意）
          </span>
        </span>
        <Switch checked={spoilerGuard} onCheckedChange={setSpoilerGuard} />
      </label>

      {spoilerGuard && (
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            {includeCurrent ? '今読んでいる章まで' : '読み終えた章まで'}に登場したものを表示しています
          </p>
          {!includeCurrent && currentSectionIndex >= 0 && (
            <Button variant="ghost" size="sm" className="shrink-0 px-2!" onClick={() => setIncludeCurrent(true)}>
              <Eye className="size-4" />
              この章も
            </Button>
          )}
        </div>
      )}
      {spoilerGuard && includeCurrent && (
        <p className="text-xs text-amber-600">この章の説明には、章の後半の内容が含まれる場合があります</p>
      )}

      {relations.length > 0 && (
        <div className="grid grid-cols-2 gap-1 rounded-lg bg-muted p-1">
          {(['list', 'graph'] as View[]).map((v) => (
            <button
              key={v}
              type="button"
              className={cn(
                'rounded-md py-1.5 text-xs',
                view === v ? 'bg-background font-medium shadow-sm' : 'text-muted-foreground',
              )}
              onClick={() => setView(v)}
            >
              {v === 'list' ? '一覧' : '相関図'}
            </button>
          ))}
        </div>
      )}

      {view === 'graph' && relations.length > 0 ? (
        <RelationGraph relations={relations} entries={entries} />
      ) : (
        <>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="人物・用語を探す"
              className="pl-8"
            />
          </div>

          {groups.length > 1 && (
            <div className="flex flex-wrap gap-1.5">
              <FilterChip active={kindFilter === 'all'} onClick={() => setKindFilter('all')}>
                すべて {filtered.length}
              </FilterChip>
              {groups.map((group) => (
                <FilterChip
                  key={group.kind}
                  active={kindFilter === group.kind}
                  onClick={() => setKindFilter(group.kind)}
                >
                  <group.icon className="size-3.5" />
                  {group.label} {group.entries.length}
                </FilterChip>
              ))}
            </div>
          )}

          {visibleGroups.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {entries.length === 0 ? 'まだ登場人物・用語がありません' : '見つかりませんでした'}
            </p>
          ) : (
            visibleGroups.map((group) => (
              <section key={group.kind} className="space-y-0.5">
                <h3 className="flex items-center gap-1.5 border-b pb-1 text-xs font-semibold text-muted-foreground">
                  <group.icon className="size-3.5" />
                  {group.label}（{group.entries.length}）
                </h3>
                <ul className="divide-y">
                  {group.entries.map((entry) => (
                    <GlossaryItem
                      key={entry.name}
                      entry={entry}
                      occurrences={occurrencesFor(entry.name)}
                      relations={relationsOf(relations, entry)}
                      onNavigate={onNavigate}
                    />
                  ))}
                </ul>
              </section>
            ))
          )}
        </>
      )}
    </div>
  )
}
